import React from "react";
import { ButtonComponent } from "@/components";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutesPathList } from "@/config/routes/Routes.tsx";
import { CreateTransaction, Transaction } from "@/types/Transaction.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, ApiResponse } from "@/types/Api.tsx";
import { ValidationErrors } from "@/types/ValidationError.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import CustomLogger from "@/utils/CustomLogger.tsx";
import { GetTransactionsQueryKey } from "@/config/constants/QueryKeys.tsx";
import { getData } from "@/utils/localStorage.tsx";
import { clientPosKey } from "@/config/constants/StorageKeys.tsx";
import { isSNS } from "@/utils/utils";
import bgSNS from "../assets/SVG/bgSNS.svg";
import transactionSNS from "../assets/SVG/transactionSNS.svg";

const TransactionApproved: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const state = location.state as {
    transaction_id: string;
    cashInCase?: boolean;
    txData: CreateTransaction;
    isComingFromTransactionApproved?: boolean;
  };

  const clientData = getData<{ client_id: number | null }>(clientPosKey);

  const checkTransactionMutation = useMutation<
    ApiResponse<Transaction>,
    ApiError<ValidationErrors>,
    CreateTransaction
  >({
    mutationFn: (data) =>
      GenericApi.post<Transaction>(ApiRoutes.TRANSACTION_CHECK, {
        ...data,
        client_id: clientData?.client_id,
      }),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const handleCreateTransaction = (data: CreateTransaction) => {
    checkTransactionMutation.mutate(data, {
      onSuccess: async () => {
        createTransactionMutation.mutate(data, {
          onSuccess: async () => {
            queryClient.invalidateQueries({
              queryKey: [GetTransactionsQueryKey],
            });
          },
        });
      },
      onError: async ({ error }) => {
        CustomLogger.error(error);
      },
    });
  };

  const createTransactionMutation = useMutation<
    ApiResponse<Transaction>,
    ApiError<ValidationErrors>,
    CreateTransaction
  >({
    mutationFn: (data) =>
      GenericApi.post<Transaction>(ApiRoutes.TRANSACTIONS, { ...data }),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      navigate(RoutesPathList.WaintingApproval, {
        state: {
          transaction_id: state?.isComingFromTransactionApproved
            ? state?.transaction_id
            : data?.id,
        },
      });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  return !isSNS() ? (
    <div className="overflow-hidden min-h-screen font-satoshi w-full bg-[#F3F3F3] flex items-center flex-col justify-between p-12">
      <div className="flex justify-center h-full flex-col items-center gap-10">
        <div className="text-center">
          <h1
            className={`text-[1.6875rem] mt-[3rem] font-[700] ${
              !(state.cashInCase && !state?.isComingFromTransactionApproved)
                ? "text-[#62C75D]"
                : ""
            }`}
          >
            {state?.cashInCase && !state?.isComingFromTransactionApproved
              ? "Please confirm you have received the funds"
              : "Transaction Approved!"}
          </h1>
        </div>

        {!(state?.cashInCase && !state?.isComingFromTransactionApproved) ? (
          <svg
            width="66"
            height="66"
            viewBox="0 0 66 66"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M65.22 32.61C65.22 50.62 50.62 65.22 32.61 65.22C14.6 65.22 0 50.62 0 32.61C0 14.6 14.6 0 32.61 0C50.62 0 65.22 14.6 65.22 32.61ZM41 34.97C41.43 34.54 41.85 34.17 42.54 33.46C47.06 28.81 54.6 24.49 44.93 20.08C41.64 19.55 31.27 32.6 28.08 34.97C26 33.43 23.52 29.25 21.09 28.93C17.9 28.51 14.59 30.08 14.55 33.47C14.51 36.18 24.68 46.45 27.23 46.86C29.78 47.27 36.83 39.09 39.06 36.92C39.77 36.23 39.63 36.24 40.5 35.5L41 34.97Z"
              fill="#62C75D"
            />
          </svg>
        ) : null}

        <div className="text-center mt-[1rem]">
          <h1 className="text-[1.6875rem] font-[700] mb-4">
            Did You Complete the Exchange?
          </h1>

          <p className="text-[#C0C0C0] font-[500]">
             Please confirm that your <br/> exchange has been <br/> successfully completed.
          </p>
        </div>
      </div>

      <ButtonComponent
        title={state?.cashInCase ? "Next" : "Confirm"}
        actionCallback={() => {
          if (state?.cashInCase) {
            handleCreateTransaction(state?.txData);
            return;
          }

          navigate(RoutesPathList.ExchangeSuccess, {
            state: {
              transaction_id: state?.transaction_id,
              isComingFromTransactionApproved: true,
            },
          });
        }}
        name={`button--classic w-[10.5rem] mt-[2rem] text-center !bg-[#222b45] !text-white`}
      />

      <ButtonComponent
        title={"Exit"}
        actionCallback={() =>
          navigate(RoutesPathList.NewClientPOS, { replace: true })
        }
        name={`w-[10.5rem] text-center !text-[#202A43] font-[700]`}
      />
    </div>
  ) : (
    <div
      className="overflow-hidden min-h-screen font-[helvetica] w-full bg-[#1C1C1C] pb-[2.6rem] flex flex-col justify-between items-center"
      style={{
        backgroundImage: `url(${bgSNS})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
    >
      <div className="flex justify-center h-full flex-col items-center gap-10">
        <div className="text-center">
          <h1
            className={`text-[1.6875rem] mt-[3rem] font-[700] font-[helvetica] ${
              !(state.cashInCase && !state?.isComingFromTransactionApproved)
                ? "bg-clip-text text-transparent"
                : ""
            }`}
            style={
              !(state.cashInCase && !state?.isComingFromTransactionApproved)
                ? {
                    backgroundImage:
                      "linear-gradient(36deg, #DA3BC8 -2.28%, #8AFEFD 100.54%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }
                : {
                    backgroundImage:
                      "linear-gradient(36deg, #DA3BC8 -2.28%, #8AFEFD 100.54%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }
            }
          >
            {state?.cashInCase && !state?.isComingFromTransactionApproved ? (
              "Please confirm you have received the funds"
            ) : (
              <>
                Transaction
                <br />
                Approved!
              </>
            )}
          </h1>
        </div>

        {!(state?.cashInCase && !state?.isComingFromTransactionApproved) ? (
          <img src={transactionSNS} />
        ) : null}

        <div className="text-center mt-[1rem]">
          <h1 className="text-[1.6875rem] font-[700] font-[helvetica] text-white mb-4">
            Did You Complete <br/> the Exchange?
          </h1>

          <p className="text-[#7A7A7A]">
            Please confirm that your <br/> exchange has been <br/> successfully completed.
          </p>
        </div>
      </div>

      <ButtonComponent
        title={state?.cashInCase ? "Next" : "Confirm"}
        actionCallback={() => {
          if (state?.cashInCase) {
            handleCreateTransaction(state?.txData);
            return;
          }

          navigate(RoutesPathList.ExchangeSuccess, {
            state: {
              transaction_id: state?.transaction_id,
              isComingFromTransactionApproved: true,
            },
          });
        }}
        name={`button_pink mt-[2rem] !w-[19.4375rem] text-white text-center`}
      />

      <ButtonComponent
        title={"Exit"}
        actionCallback={() =>
          navigate(RoutesPathList.NewClientPOS, { replace: true })
        }
        name={`text-[#EE33D9] font-[helvetica] font-[600]`}
      />
    </div>
  );
};

export default TransactionApproved;
